import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getUsers } from "../api/users.api";
import { defaultUserFilters, type UserFilters, type UserItem } from "../types/user.types";

export interface UseGetUserListReturn {
  users: UserItem[];
  loading: boolean;
  error: string | null;
  filters: UserFilters;
  updateFilters: Dispatch<SetStateAction<UserFilters>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalCount: number;
  refetch: () => Promise<void>;
}

export const USER_LIST_PAGE_SIZE = 20;
function formatDateOnly(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function useGetUserList(): UseGetUserListReturn {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [filters, setFilters] = useState<UserFilters>(defaultUserFilters);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();
  const request = useMemo(
    () => ({
      index: page - 1,
      size: USER_LIST_PAGE_SIZE,
      ...(filters.name ? { name: filters.name } : {}),
      ...(filters.email ? { email: filters.email } : {}),
      ...(filters.role ? { role: filters.role } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.age !== null ? { age: filters.age } : {}),
      ...(filters.date_of_birth ? { date_of_birth: formatDateOnly(filters.date_of_birth) } : {}),
      ...(filters.join_date.from ? { join_date_from: filters.join_date.from } : {}),
      ...(filters.join_date.to ? { join_date_to: filters.join_date.to } : {}),
    }),
    [
      filters.age,
      filters.date_of_birth,
      filters.email,
      filters.name,
      filters.join_date.from,
      filters.join_date.to,
      filters.role,
      filters.status,
      page,
    ],
  );

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const result = await run((signal) => getUsers(request, signal));
      if (result) {
        setUsers(result.list);
        setTotalCount(result.paging.count);
      }
    } catch (requestError: unknown) {
      setUsers([]);
      setTotalCount(0);
      setError(getApiErrorMessage(requestError, "Failed to load users"));
    }
  }, [request, run]);

  useEffect(() => {
    const timer = window.setTimeout(() => void refetch(), 400);
    return () => window.clearTimeout(timer);
  }, [refetch]);

  const updateFilters = useCallback((nextFilters: SetStateAction<UserFilters>) => {
    setFilters(nextFilters);
    setPage(1);
  }, []);

  return { users, loading, error, filters, updateFilters, page, setPage, totalCount, refetch };
}
